import { useMutation } from '@tanstack/react-query';
import { uploadAttachment, uploadDirectlyToS3 } from '../../../../services/general';

const IMAGE_TYPE_PREFIX = 'image/';
const PDF_TYPE = 'application/pdf';

export const isImageFile = (file) => file.type.startsWith(IMAGE_TYPE_PREFIX);

// Some browsers give a PDF an empty type, so fall back to the file extension.
export const isPdfFile = (file) =>
  file.type === PDF_TYPE || (!file.type && file.name?.toLowerCase().endsWith('.pdf'));

export const isAllowedAttachment = (file) => isImageFile(file) || isPdfFile(file);

export function useUploadTicketAttachment({ onSuccess, onError } = {}) {
  return useMutation({
    mutationFn: async ({ file, folderName }) => {
      if (!isAllowedAttachment(file)) {
        throw new Error('Only image and PDF files can be attached.');
      }
      if (!folderName?.trim()) {
        throw new Error('A folder name is required before uploading.');
      }

      // The S3 policy matches Content-Type exactly, so always send a real type.
      const contentType = file.type || PDF_TYPE;

      // Step 1 — sign
      const { documentId, uploadUrl, uploadFields } = await uploadAttachment({
        fileName: file.name,
        fileSize: file.size,
        contentType,
        folderName,
        rootFolder: 'ticket_docs', // fixed — ticket attachments always live here
      });

      // Step 2 — upload straight to S3. "file" must be the LAST field.
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      await uploadDirectlyToS3(uploadUrl, formData);

      // S3 returns 204 No Content — carry forward what we already know.
      return {
        documentId,
        name: file.name,
        fileSize: file.size,
        applicationType: contentType,
      };
    },
    onSuccess,
    onError,
  });
}
